import { z } from "zod";
import { buildHref } from "@softmax/web-addresses";

const searchString = z.preprocess(
  (value) => (Array.isArray(value) ? value[0] : value),
  z.union([z.string(), z.number(), z.boolean()]).transform(String).optional(),
);

export const returnSearchSchema = z.object({ redirect_url: searchString });
export const signInSearchSchema = returnSearchSchema.extend({
  error: z
    .enum([
      "oauth_failed",
      "account_in_use",
      "account_not_linked",
      "session_changed",
    ])
    .optional(),
});
export const cliSearchSchema = z.object({ callback: searchString });
export const cliIssueSchema = cliSearchSchema.extend({
  sessionUserId: z.string().min(1),
});
export const cliCompleteSearchSchema = cliSearchSchema.extend({
  code: searchString,
});
export const githubConnectSearchSchema = z.object({
  account: searchString,
  error: searchString,
  connected: searchString,
});

export function resolveRedirectUrl(value: string | undefined): string {
  const candidate = value?.trim();

  if (
    !candidate ||
    !candidate.startsWith("/") ||
    candidate.startsWith("//") ||
    candidate.includes("\\")
  ) {
    return buildHref({ name: "browse" });
  }

  const destination = new URL(candidate, "https://softmax.invalid");

  if (destination.origin !== "https://softmax.invalid") {
    return buildHref({ name: "browse" });
  }

  return destination.pathname + destination.search + destination.hash;
}

/** Sites that run the Cogent SDK. Their users approve signing in here and come back with a one-time code. */
const cogentSiteHosts: string[] = [];

export function getCliCallbackState(value: string | undefined): {
  readonly callback: string | null;
  readonly hasInvalidCallback: boolean;
} {
  if (!value) return { callback: null, hasInvalidCallback: false };

  if (!URL.canParse(value)) {
    return { callback: null, hasInvalidCallback: true };
  }

  const url = new URL(value);
  const allowed =
    (url.protocol === "http:" || url.protocol === "https:") &&
    !url.username &&
    !url.password &&
    (url.hostname === "127.0.0.1" ||
      url.hostname === "localhost" ||
      url.hostname.endsWith(".softmax-research.net") ||
      (url.protocol === "https:" && cogentSiteHosts.includes(url.hostname)));
  return {
    callback: allowed ? url.toString() : null,
    hasInvalidCallback: !allowed,
  };
}
